import React, {RefObject} from "react";
import {connect} from "react-redux";
import {ConfigOptions} from "antd/lib/message";
import {Button, message} from "antd";
import {CloseOutlined} from "@ant-design/icons";
import {ArgsProps} from "antd/es/message";
import Nullable from "@data-force/app/interfaces/nullable";
import "./MessageHolder.scss";
import StoreState from "../../interfaces/store/storeState";
import {RemoveAlertAction} from "../../interfaces/store/actions/layout";
import {removeAlert} from "../../store/actions/layout";
import {ENTER_KEY, SPACE_KEY} from "../../utils/constants";

interface StateProps {
    alerts: ArgsProps[]
}

interface DispatchProps {
    removeAlert(payload: string | number): RemoveAlertAction
}

type Props = StateProps & DispatchProps;

class MessageHolder extends React.Component<Props> {
    private messageHolder: RefObject<HTMLDivElement> = React.createRef();

    private closeAlertRefs: RefObject<HTMLButtonElement>[] = [];

    constructor(props: Props) {
        super(props);

        this.configureMessages = this.configureMessages.bind(this);
        this.createAlerts = this.createAlerts.bind(this);
    }

    componentDidMount(): void {
        this.configureMessages();
    }

    componentDidUpdate(): void {
        const {alerts}: Props = this.props;
        this.createAlerts();

        this.closeAlertRefs = alerts.map(() => React.createRef());
        if (this.closeAlertRefs.length) {
            const lastAlertIndex: number = this.closeAlertRefs.length - 1;
            const lastAlertClose: Nullable<HTMLButtonElement> = this.closeAlertRefs[lastAlertIndex].current;
            lastAlertClose?.focus();
        }
    }

    /**
     * Close alert
     *
     * @param {React.KeyboardEvent} event
     * @param {Nullable<string | number>} key
     * @return {void}
     */
    onCloseKeyDown(key: Nullable<string | number>, event: React.KeyboardEvent): void {
        if ([ENTER_KEY, SPACE_KEY].includes(event.key)) {
            this.closeAlert(key);
        }
    }

    /**
     * Global antd message configuration.
     *
     * @return {void}
     */
    configureMessages(): void {
        const options: ConfigOptions = {
            duration: 0,
            getContainer: () => this.messageHolder.current!,
            maxCount: 10,
        };
        message.config(options);
    }

    /**
     * Close single alert
     *
     * @param {Nullable<string | number>} key
     * @return {void}
     */
    closeAlert(key: Nullable<string | number>): void {
        if (key) {
            message.destroy(key);
            this.props.removeAlert(key);
        }
    }

    /**
     * Creates content of a single alert.
     *
     * @param {ArgsProps} alert
     * @param {number} index
     */
    createAlertContent(alert: ArgsProps, index: number): React.ReactNode {
        const {content, key}: ArgsProps = alert;
        return (
            <span>
                {content}
                <Button
                    className="df-close-alert"
                    tabIndex={0}
                    onClick={() => this.closeAlert(key)}
                    onKeyDown={this.onCloseKeyDown.bind(this, key)}
                    ref={this.closeAlertRefs[index]}
                >
                    <CloseOutlined/>
                </Button>
            </span>
        );
    }

    /**
     * Create screen presentation for each alert in store.
     *
     * @return {void}
     */
    createAlerts() {
        const {alerts}: Props = this.props;
        alerts.forEach((alert: ArgsProps, index: number): void => {
            const config: ArgsProps = {...alert};
            config.content = this.createAlertContent(config, index);
            config.className = `df-message-${config.type}`;
            config.icon = <div className="df-icon-message"/>;
            switch (config.type) {
                case "error":
                    message.error(config);
                    break;
                case "info":
                    message.info(config);
                    break;
                case "loading":
                    message.loading(config);
                    break;
                case "success":
                    message.success(config);
                    break;
                case "warning":
                    message.warning(config);
                    break;
                default:
                    break;
            }
        });
    }

    render() {
        return (
            <div
                className="df-message-holder"
                ref={this.messageHolder}
                role="alert"
            />
        );
    }
}

function mapStateToProps({layout}: StoreState): StateProps {
    return {
        alerts: layout.content.alerts,
    };
}

const mapDispatchToProps: DispatchProps = {
    removeAlert,
};

export default connect(mapStateToProps, mapDispatchToProps)(MessageHolder);
